import { API_BASE_URL } from './api-config';
import { ApiError, defaultMessageForStatus } from './http-error';
import type { ApiErrorBody } from '@/shared/types/api.types';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  readonly method?: HttpMethod;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly body?: unknown;
  readonly headers?: Readonly<Record<string, string>>;
  readonly skipAuth?: boolean;
  readonly signal?: AbortSignal;
}

/** Fournisseur du token courant (branché par la feature auth). */
export type AccessTokenProvider = () => string | null;

/** Tentative de refresh silencieux ; résout true si un nouveau token est disponible. */
export type RefreshHandler = () => Promise<boolean>;

let accessTokenProvider: AccessTokenProvider = () => null;
let refreshHandler: RefreshHandler | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function configureHttpClient(config: {
  readonly getAccessToken: AccessTokenProvider;
  readonly onUnauthorized?: RefreshHandler;
}): void {
  accessTokenProvider = config.getAccessToken;
  refreshHandler = config.onUnauthorized ?? null;
}

function resolveUrl(endpoint: string, query?: RequestOptions['query']): string {
  const base = API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${base.replace(/\/+$/, '')}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function buildHeaders(options: RequestOptions): Headers {
  const headers = new Headers(options.headers as HeadersInit | undefined);
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');

  if (!options.skipAuth) {
    const token = accessTokenProvider();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

async function parseJson<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

async function parseError(response: Response, payload: unknown): Promise<ApiError> {
  const body = isRecord(payload) ? (payload as ApiErrorBody) : undefined;
  const message = body?.message || defaultMessageForStatus(response.status);
  const code = body?.code || `HTTP_${response.status}`;
  return new ApiError(response.status, code, message, payload);
}

async function attemptRefresh(): Promise<boolean> {
  if (!refreshHandler) return false;
  if (!refreshInFlight) {
    refreshInFlight = refreshHandler().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Headers d'authentification Bearer partagés (multipart/binaire). */
function authHeaders(skipAuth: boolean | undefined, extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  if (!skipAuth) {
    const token = accessTokenProvider();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

/** Requête « non-JSON » (multipart ou binaire) avec refresh silencieux unique. */
async function performRaw(
  endpoint: string,
  init: {
    method: HttpMethod;
    body?: BodyInit;
    headers?: HeadersInit;
    skipAuth?: boolean;
    signal?: AbortSignal;
  },
  mode: 'blob' | 'text',
  isRetry: boolean,
): Promise<Response> {
  const headers = authHeaders(init.skipAuth, init.headers);
  const response = await fetch(endpoint, {
    method: init.method,
    headers,
    signal: init.signal,
    body: init.body,
  });

  if (response.status === 401 && !init.skipAuth && !isRetry) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      return performRaw(endpoint, init, mode, true);
    }
  }
  if (!response.ok) {
    const payload = await parseJson<unknown>(response);
    throw await parseError(response, payload);
  }
  if (mode === 'blob') return response;
  return response;
}

async function perform<T>(endpoint: string, options: RequestOptions, isRetry: boolean): Promise<T> {
  const method: HttpMethod = options.method ?? 'GET';
  const url = resolveUrl(endpoint, options.query);
  const headers = buildHeaders(options);

  const response = await fetch(url, {
    method,
    headers,
    signal: options.signal,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 401 && !options.skipAuth && !isRetry) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      // Nouvelle tentative unique avec le token rafraîchi.
      return perform<T>(endpoint, options, true);
    }
  }

  const payload = await parseJson<unknown>(response);
  if (!response.ok) {
    throw await parseError(response, payload);
  }
  return payload as T;
}

function sendHttpRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  return perform<T>(endpoint, options, false);
}

export const httpClient = {
  get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return sendHttpRequest<T>(endpoint, { ...options, method: 'GET' });
  },
  post<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return sendHttpRequest<T>(endpoint, { ...options, method: 'POST', body });
  },
  put<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return sendHttpRequest<T>(endpoint, { ...options, method: 'PUT', body });
  },
  patch<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return sendHttpRequest<T>(endpoint, { ...options, method: 'PATCH', body });
  },
  delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return sendHttpRequest<T>(endpoint, { ...options, method: 'DELETE' });
  },
  /**
   * POST multipart (FormData) — le Content-Type est posé par le navigateur
   * (boundary). Réponse JSON typique du contrat package v3.
   */
  async postMultipart<T>(
    endpoint: string,
    input: { fields?: Readonly<Record<string, string>>; file?: { field: string; file: File } },
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    const form = new FormData();
    for (const [key, value] of Object.entries(input.fields ?? {})) {
      form.append(key, value);
    }
    if (input.file) form.append(input.file.field, input.file.file);
    const url = resolveUrl(endpoint, options?.query);
    const response = await performRaw(
      url,
      {
        method: 'POST',
        body: form,
        skipAuth: options?.skipAuth,
        signal: options?.signal,
      },
      'text',
      false,
    );
    const payload = await parseJson<unknown>(response);
    if (!response.ok) throw await parseError(response, payload);
    return payload as T;
  },
  /**
   * GET binaire authentifié (téléchargement de pièce jointe) — retourne le
   * Blob + headers (Content-Type/Content-Disposition) de la réponse.
   */
  async downloadBinary(
    endpoint: string,
    options?: { query?: RequestOptions['query']; signal?: AbortSignal; skipAuth?: boolean },
  ): Promise<{ blob: Blob; contentType: string; contentDisposition: string | null }> {
    const url = resolveUrl(endpoint, options?.query);
    const response = await performRaw(
      url,
      { method: 'GET', skipAuth: options?.skipAuth, signal: options?.signal },
      'blob',
      false,
    );
    const blob = await response.blob();
    return {
      blob,
      contentType: response.headers.get('Content-Type') ?? 'application/octet-stream',
      contentDisposition: response.headers.get('Content-Disposition'),
    };
  },
};
